export type CtaMissingValueStrategy = 'empty' | 'omit' | 'hide';

export interface CtaInterpolationSources {
  lead?: object;
  partnerIdentifiers?: Record<string, string>;
}

export interface InterpolateCtaUrlOptions {
  baseUrl: string;
  staticParams?: Record<string, string>;
  fieldMap?: Record<string, string>;
  sources: CtaInterpolationSources;
  missingValue?: CtaMissingValueStrategy;
}

export type InterpolatedCtaUrl = { href: string } | { hide: true };

export function interpolateCtaUrl({
  baseUrl,
  staticParams,
  fieldMap,
  sources,
  missingValue = 'empty',
}: InterpolateCtaUrlOptions): InterpolatedCtaUrl {
  const url = parseHttpUrl(baseUrl);
  if (!url) return { hide: true };

  const params: Array<[string, string]> = Object.entries(staticParams ?? {});

  for (const [queryKey, sourcePath] of Object.entries(fieldMap ?? {})) {
    const value = resolveSourceValue(sources, sourcePath);
    if (value === undefined || value === null) {
      if (missingValue === 'hide') return { hide: true };
      if (missingValue === 'omit') continue;
      params.push([queryKey, '']);
      continue;
    }
    params.push([queryKey, String(value)]);
  }

  if (params.length > 0) {
    const appendedQuery = params
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
      )
      .join('&');
    const existingQuery = url.search.slice(1);
    url.search = existingQuery
      ? `${existingQuery}&${appendedQuery}`
      : appendedQuery;
  }

  return { href: url.toString() };
}

function parseHttpUrl(baseUrl: string): URL | undefined {
  try {
    const url = new URL(baseUrl);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url
      : undefined;
  } catch {
    return undefined;
  }
}

function resolveSourceValue(
  sources: CtaInterpolationSources,
  sourcePath: string,
): unknown {
  const [root, ...segments] = sourcePath.split('.');
  if (
    (root !== 'lead' && root !== 'partnerIdentifiers') ||
    segments.length === 0
  ) {
    return undefined;
  }

  let current: unknown = sources[root];
  for (const segment of segments) {
    if (
      !isRecord(current) ||
      !Object.prototype.hasOwnProperty.call(current, segment)
    ) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
